import { ApiError } from '@/lib/api';

// What to actually SAY when a request fails.
//
// The rule this exists to enforce: never throw away a message the server took the trouble to write.
// A 402 that names the plan limit, a 409 that names the campaigns still using a media item, a 502
// carrying the AI provider's own reason — each of those is more useful than anything generic, and
// each was being replaced by a sentence like "Could not create a campaign".
//
// Pure on purpose: no React, no router, no DOM, no toast. The action it returns is SYMBOLIC rather
// than a URL, because the route depends on the workspace and only the hook knows that.

export type ApiErrorActionKind = 'billing' | 'signin';

export interface ApiErrorAction {
  label: string;
  kind: ApiErrorActionKind;
}

export interface ApiErrorDescription {
  /** The headline. Short, and specific whenever the server gave us something specific. */
  title: string;
  /** The sentence underneath: context, or what to do about it. */
  description?: string;
  action?: ApiErrorAction;
}

export interface DescribeOptions {
  /** What this action was trying to do, in the caller's own words. Used when nothing better exists. */
  fallback: string;
  /**
   * Whether this user can actually reach /billing. It sits behind `RequireRole permission=
   * "billing:read"`, which `<Navigate>`s away — so offering "View plans" to a content or media role
   * sends them to a silent redirect.
   */
  canManageBilling?: boolean;
}

// Human names for the plan limits, so the copy can say "campaigns" rather than "campaigns limit".
const LIMIT_NOUNS: Record<string, string> = {
  campaigns: 'campaigns',
  contacts: 'contacts',
  users: 'team members',
  workspaces: 'workspaces',
};

const VIEW_PLANS: ApiErrorAction = { label: 'View plans', kind: 'billing' };
const SIGN_IN: ApiErrorAction = { label: 'Sign in', kind: 'signin' };

/**
 * A message the server wrote for a person, as opposed to a framework's text. Joi's output reads
 * like a stack trace ('"name" must be a string') and belongs under a human title; everything else
 * we emit is a real sentence and should be the title itself.
 */
const isMachineText = (message?: string) => !message
  || /^"/.test(message)
  || message === 'Internal server error'
  || message === 'Unknown error'
  || message === 'Not found'
  || message === 'Validation error';

const upgradeHint = (canManageBilling?: boolean) => (canManageBilling
  ? { description: 'Upgrade for a higher limit.', action: VIEW_PLANS }
  : { description: 'Ask a workspace owner or admin to upgrade the plan.' });

function describeLimitReached(error: ApiError, canManageBilling?: boolean): ApiErrorDescription {
  const noun = LIMIT_NOUNS[error.limitKey || ''] || error.limitKey || 'items';
  const title = typeof error.limit === 'number'
    ? `You've used all ${error.limit} ${noun} on your plan`
    : `You've reached your plan limit for ${noun}`;

  // The drafts sentence is load-bearing rather than padding. A campaign row is created the moment
  // somebody clicks "New campaign", so the count includes drafts that were never configured or
  // launched — which is not visible from the campaigns list, and is the usual reason somebody hits
  // this wall while certain they do not have that many campaigns. It is also true advice: paranoid
  // mode is on globally, so `Campaign.count()` excludes soft-deleted rows and deleting frees a slot.
  const draftsNote = error.limitKey === 'campaigns'
    ? 'Drafts you never launched count towards this — deleting one frees a slot. '
    : '';

  return canManageBilling
    ? { title, description: `${draftsNote}Upgrade for a higher limit.`, action: VIEW_PLANS }
    : { title, description: `${draftsNote}Ask a workspace owner or admin to upgrade the plan.` };
}

function describeByCode(error: ApiError, options: DescribeOptions): ApiErrorDescription | null {
  const { canManageBilling } = options;

  switch (error.code) {
    case 'limit_reached':
      return describeLimitReached(error, canManageBilling);

    case 'subscription_required':
      return {
        title: 'This workspace needs an active subscription',
        // The first fear on hitting this wall is that the work is gone. It is not: a lapsed
        // workspace can still open its campaigns and media, it just cannot start new sends.
        description: canManageBilling
          ? 'Everything you have made is still here — subscribe to start sending again.'
          : 'Everything you have made is still here. Ask a workspace owner or admin to subscribe.',
        ...(canManageBilling ? { action: VIEW_PLANS } : {}),
      };

    case 'feature_not_in_plan':
      return {
        title: 'That is not included in your plan',
        ...upgradeHint(canManageBilling),
      };

    case 'insufficient_credits':
      return {
        // The server's sentence names the exact numbers ("Required: 42, available: 7"), which is
        // the whole point of it.
        title: 'Not enough credits',
        description: error.message,
        ...(canManageBilling ? { action: VIEW_PLANS } : {}),
      };

    case 'insufficient_permissions':
      return {
        title: 'You do not have permission to do that',
        description: 'Ask a workspace owner or admin to change your role.',
      };

    case 'validation_error': {
      // Joi's text names the offending field but reads like a compiler diagnostic, so the caller's
      // own description of the action stays the title and the field detail goes underneath.
      const fields = error.body?.errors?.map((d) => d.message).filter(Boolean) ?? [];
      const detail = fields.length ? fields.join('. ') : error.message;
      return { title: options.fallback, description: detail };
    }

    default:
      return null;
  }
}

function describeByStatus(error: ApiError, options: DescribeOptions): ApiErrorDescription {
  const { fallback } = options;
  const serverSaid = !isMachineText(error.message) ? error.message : undefined;

  switch (error.status) {
    case 401:
      return {
        title: 'Your session has expired',
        description: 'Sign in again to continue.',
        action: SIGN_IN,
      };

    case 403:
      return {
        title: serverSaid || 'You do not have permission to do that',
        description: serverSaid ? undefined : 'Ask a workspace owner or admin to change your role.',
      };

    case 404:
      return {
        title: serverSaid || fallback,
        description: serverSaid ? undefined : 'It may have been deleted — try reloading the page.',
      };

    case 409:
      // The server's 409s are the best messages in the codebase: deleteMedia's names the campaigns
      // still using the item, and the contacts one names the contact already holding the number.
      // Overriding either of those with something generic would be a straight downgrade.
      return { title: serverSaid || fallback };

    case 429:
      return {
        title: 'Too many requests',
        description: 'Give it a moment and try again.',
      };

    default:
      break;
  }

  if (error.status >= 500) {
    // A 5xx message survives only when the server marked it `expose` — a generation failure
    // carrying the provider's reason, for instance. Otherwise it has been erased to
    // "Internal server error", which tells nobody anything.
    return serverSaid
      ? { title: serverSaid }
      : { title: fallback, description: 'Something went wrong on our side. Try again in a moment.' };
  }

  return { title: serverSaid || fallback };
}

// What each browser says when the request never left. `fetch` rejects with a TypeError, and a
// TypeError IS an Error — which is why the `error instanceof Error ? error.message` pattern this
// replaces showed the user the literal string "Failed to fetch" (or "Load failed" on Safari).
const NETWORK_MESSAGES = /failed to fetch|networkerror|load failed|network request failed/i;

// Matched on the message rather than on the type alone: a TypeError from our OWN code (reading a
// property of undefined, say) is a bug, and telling the user to check their connection would send
// them after the wrong thing entirely.
const isNetworkError = (error: unknown): boolean => error instanceof TypeError
  && NETWORK_MESSAGES.test(error.message || '');

function describeNetworkError(fallback: string): ApiErrorDescription {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  return offline
    ? { title: "You're offline", description: 'Reconnect and try again — nothing was sent.' }
    : { title: "Couldn't reach the server", description: `${fallback} Check your connection and try again.` };
}

/**
 * Turn whatever was thrown into something worth reading.
 *
 * Precedence is code → status → the server's own sentence → the caller's fallback, deciding PER
 * BRANCH whether the server's message becomes the title or the description. A blanket rule would be
 * wrong in both directions.
 */
export function describeApiError(error: unknown, options: DescribeOptions): ApiErrorDescription {
  if (!(error instanceof ApiError)) {
    if (isNetworkError(error)) return describeNetworkError(options.fallback);
    // Something thrown by our own code. Its message is for us, not for the user.
    return { title: options.fallback };
  }

  return describeByCode(error, options) ?? describeByStatus(error, options);
}
